import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot} from '@angular/router';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {BaseService} from '@shared/services/api/base-service';
import {PagedModel} from '@shared/api/model/paged-model';
import {Impact, Priority, RULE_CATEGORIES, Template, TemplateDraftSearchTerms, TemplateId, TemplateSearchTerms} from '../api/template';
import {SensorValue} from './template-editor-utilities';
import * as gql from "gql-query-builder";
import {GRAPHQL_TYPES, HttpUtilityService} from "@shared/services/api/http-utility-service";
import {RulesService} from "@app/layout/rules/rulesmanager/rules.service";
import {PageInfo} from '@shared/components/ace-table/ace-table.component';
import Fields from 'gql-query-builder/build/Fields';
import {APIResponse} from '@app/layout/admin/services/api/api.interface';
import {CacheByParams} from '@shared/data-stores/cache-by-params';
import {Metadata, MetadataId, MetadataTypeId} from '@app/graphql-typings/graphql';
import {MapUtils, SetUtils} from '@app/shared/util/collections/collection-utils';
import {SiteId} from '@app/shared/services/api/access-sites.interface';

export interface TemplateAndUsage {
  template: Template;
  originalTemplate: Template;
  taskCount: number;
}

export type TemplateNameAndId = Pick<Template, 'template_name'| 'template_id'>;
export type TemplateNameAndDraftId = Pick<Template, 'template_name'| 'draft_id'>;

export interface TemplateScopeOptions {
  getSensors?: boolean;
  getFunctions?: boolean;
  getProperties?: boolean;
  getFaults?: boolean;
  getGates?: boolean;
  getRelations?: boolean;
}

export enum OPERATION {
  TEMPLATE = 'template',
  TEMPLATES = 'templates',
  TEMPLATE_DRAFT = 'template_draft',
  TEMPLATE_DRAFTS = 'template_drafts',
  IMPACTS = 'impacts'
}

export interface TemplateMetadataTypeMapping {
  templateIdsByMetadataTypeId: Map<MetadataTypeId, Set<TemplateId>>;
  metadataTypeIdsByTemplateId: Map<TemplateId, Set<MetadataTypeId>>;
}

export enum TemplateResolverAction {CREATE = 'Create', EDIT = 'Edit', VIEW = 'View', DELETE = 'Delete'}
export enum TemplateResolverTarget {DRAFT = 'Draft', DRAFTS = 'Drafts', TEMPLATE = 'Template'}

export const templateAndUsageResolver: ResolveFn<TemplateAndUsage> = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<TemplateAndUsage> => {
  const id: string = route.paramMap.get('id');
  const templateService: TemplatesService = inject(TemplatesService);
  const rulesService: RulesService = inject(RulesService);
  if (route?.data?.target === TemplateResolverTarget.DRAFT) {
    return templateService.getTemplateDraft(id).pipe(mergeMap((template: Template) => {
      if (template.template_id) {
        const taskCount$: Observable<number> = rulesService.getTasksCountByTemplateId([template.template_id]);
        return forkJoin([templateService.getTemplate(template.template_id), taskCount$])
          .pipe(map(([originalTemplate, taskCount]: [Template, number]) => {
            return {template: template, originalTemplate: originalTemplate, taskCount: taskCount};
          }));
      } else {
        return of({template: template, originalTemplate: null, taskCount: 0});
      }
    }));
  } else {
    const taskCount$: Observable<number> = rulesService.getTasksCountByTemplateId([id]);
    return forkJoin([templateService.getTemplate(id), taskCount$])
      .pipe(map(([template, taskCount]: [Template, number]) => {
        return {template: template, originalTemplate: <Template>{...template}, taskCount: taskCount};
      }));
  }
};

@Injectable({providedIn: 'root'})
export class TemplatesService extends BaseService {
  private readonly PAGE_SIZE = 400;
  private readonly logger: AceLogger;
  private headers: HttpHeaders = this.getHeaders();
  private cachedImpacts: CacheByParams<[], Impact[], string>;
  private cachedPriorities: CacheByParams<[], Priority[], string>;

  constructor(private http: HttpClient,
              private httpUtilityService: HttpUtilityService,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(TemplatesService.name);
    this.cachedImpacts = new CacheByParams(() => this.fetchImpacts(), () => '');
    this.cachedPriorities = new CacheByParams(() => this.fetchAllPriorities(), () => '');
  }

  private constructTemplateFromResponseData(responseData: any): Template {
    const {category, ...rest} = responseData;
    const template = Object.assign(new Template(), rest);
    this.fixCategories(template, responseData);
    return template;
  }

  private constructTemplateFromResponse(response: any, templateResponseKey: string): Template {
    return this.constructTemplateFromResponseData(this.httpUtilityService.extractResponseData(response, templateResponseKey));
  }

  // TODO Push this into task manager
  private fixCategories(template: Template, templateData: any): void {
    if (templateData['category']) {
      const categoryNames = (<string>templateData['category']).split('/');
      template.category = categoryNames.map(categoryName => {
        const categoryObject = RULE_CATEGORIES.find(category => {
          return category.name.toLowerCase() === categoryName.toLowerCase()
            || (category.name + 's').toLowerCase() === categoryName.toLowerCase();
        });
        if (categoryObject) {
          return categoryObject.category_id;
        } else {
          this.logger.error('Found unknown template category', categoryName, 'for template', templateData['template_name']);
          return null;
        }
      }).filter(c => !!c);
    }
  }

  filterTemplate(templateById: Map<TemplateId, Template>,
                 templateIdsByMetadataTypeId: Map<MetadataTypeId, Set<TemplateId>>,
                 metadataTypeIds: MetadataTypeId[]): Map<TemplateId, Template> | null {
    if (templateById != null && (!metadataTypeIds?.length || templateIdsByMetadataTypeId != null)) {
      let filteredTemplateById: Map<TemplateId, Template>;
      if (metadataTypeIds?.length) {
        const metadataTypeGroupedTemplateIds: Set<TemplateId>[] = metadataTypeIds.map(metadataTypeId => templateIdsByMetadataTypeId.get(metadataTypeId)).filter(Boolean);
        const metadataTypeTemplateIds: Set<TemplateId> = metadataTypeGroupedTemplateIds.length
          ? metadataTypeGroupedTemplateIds.reduce(SetUtils.union)
          : new Set();
        filteredTemplateById = new Map();
        metadataTypeTemplateIds.forEach(templateId => {
          const template = templateById.get(templateId);
          if (template) {
            filteredTemplateById.set(template.template_id, template);
          }
        });
      } else {
        filteredTemplateById = new Map(templateById);
      }
      return filteredTemplateById;
    }
    return null;
  }

  buildTemplateAndMetadataTypeMaps(templateById: Map<TemplateId, Template>,
                                   metadataById: Map<MetadataId, Metadata>): TemplateMetadataTypeMapping {
    const templateIdsByMetadataTypeId = new Map<MetadataTypeId, Set<TemplateId>>();
    const metadataTypeIdsByTemplateId = new Map<TemplateId, Set<MetadataTypeId>>();
    if (templateById && metadataById) {
      templateById.forEach(template => {
        template.sensors.forEach(sensor => {
          const metadata = metadataById.get(sensor.metadata_id);
          if (metadata) {
            MapUtils.setDefault(templateIdsByMetadataTypeId, metadata.type_id, new Set()).add(template.template_id);
            MapUtils.setDefault(metadataTypeIdsByTemplateId, template.template_id, new Set()).add(metadata.type_id);
          }
        });
      });
    }
    return {templateIdsByMetadataTypeId, metadataTypeIdsByTemplateId};
  }

  getTemplate(id: string): Observable<Template> {
    const options = {headers: this.headers};
    return this.http.get(this.getGateway() + '/tasks/templates/' + id, options).pipe(map(response => {
      return this.constructTemplateFromResponse(response, OPERATION.TEMPLATE);
    }));
  }

  getTemplatesOrDraftsDependentOnMetadataType(drafts: boolean,
                                              metadata: Metadata,
                                              startIndex: number,
                                              limit: number): Observable<PagedModel<TemplateNameAndId[] | TemplateNameAndDraftId[]>> {
    let params = new HttpParams()
      .set('metadata_id', metadata.metadata_id)
      .set('start_index', startIndex)
      .set('limit', limit);
    if (metadata.type?.type_id != null) {
      params = params.set('metadata_type_id', metadata.type.type_id);
    }
    const options = {headers: this.headers, params};
    const url = '/tasks/templates' + (drafts ? '/drafts' : '') + '/metadata-validation';
    return this.http.get(this.getGateway() + url, options).pipe(map(response => {
      return this.httpUtilityService.extractPagedResponse(response, drafts ? 'affected_template_drafts' : 'affected_templates');
    }));
  }

  searchAllTemplates(searchTerm?: TemplateSearchTerms,
                     scopeOptions?: TemplateScopeOptions): Observable<Template[]> {
    const pageWrapper = (pageInfo: PageInfo) => this.searchTemplates(pageInfo, searchTerm, scopeOptions);
    return this.httpUtilityService.pageUntilComplete(pageWrapper, {first: 0, rows: this.PAGE_SIZE});
  }

  getTemplatesBySiteIds(siteIds: SiteId[]): Observable<Map<SiteId, Template[]>> {
    const templatesBySiteIdFetch = (siteIdBatch: SiteId[]) => this.searchAllTemplates({site_ids: siteIdBatch}, {getProperties: true, getSensors: true})
      .pipe(map((templates): [SiteId, Template[]][] => [[siteIdBatch[0], templates]]));
    return this.httpUtilityService.requestPartitioned<SiteId, [SiteId, Template[]]>(templatesBySiteIdFetch, siteIds, 1)
      .pipe(map(siteIdTemplatesTuples => new Map(siteIdTemplatesTuples)));
  }

  searchAllTemplateDrafts(searchTerm?: TemplateDraftSearchTerms,
                          scopeOptions?: TemplateScopeOptions): Observable<Template[]> {
    const pageWrapper = (pageInfo: PageInfo) => this.searchTemplateDrafts(pageInfo, searchTerm, scopeOptions);
    return this.httpUtilityService.pageUntilComplete(pageWrapper, {first: 0, rows: this.PAGE_SIZE});
  }

  searchTemplates(pageInfo: PageInfo,
                  searchTerm?: TemplateSearchTerms,
                  scopeOptions?: TemplateScopeOptions): Observable<PagedModel<Template[]>> {
    const templatesQuery = {
      operation: OPERATION.TEMPLATES,
      fields: this.templatesOrDraftsFields(scopeOptions, OPERATION.TEMPLATES),
      variables: {
        site_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
        primary_metadata_type_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
        template_name: {type: GRAPHQL_TYPES.STRING},
        rule_name: {type: GRAPHQL_TYPES.STRING},
        shared: {type: GRAPHQL_TYPES.BOOLEAN},
        metadata_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
        match_exact: {type: GRAPHQL_TYPES.BOOLEAN},
        match_metadata_ids_using_and: {type: GRAPHQL_TYPES.BOOLEAN},
        template_category_id: {type: GRAPHQL_TYPES.INTEGER},
        template_category_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
        start_index: {type: GRAPHQL_TYPES.INTEGER, required: true},
        limit: {type: GRAPHQL_TYPES.INTEGER, required: true}
      }
    };
    const query = gql.query(templatesQuery);
    const variables = {...{start_index: pageInfo.first, limit: pageInfo.rows}, ...searchTerm};
    return this.httpUtilityService.tasksGraphQL<Template[]>(query, templatesQuery.operation, variables).pipe(map(templatePage => ({
      data: templatePage.data.map(templateData => {
        const template = this.constructTemplateFromResponseData(templateData);
        template.is_draft = false;
        return template;
      }),
      count: templatePage.count
    })));
  }

  searchTemplateDrafts(pageInfo: PageInfo,
                       searchTerm?: TemplateDraftSearchTerms,
                       scopeOptions?: TemplateScopeOptions): Observable<PagedModel<Template[]>> {
    const templatesQuery = {
      operation: OPERATION.TEMPLATE_DRAFTS,
      fields: this.templatesOrDraftsFields(scopeOptions, OPERATION.TEMPLATE_DRAFTS),
      variables: {
        primary_metadata_type_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
        template_name: {type: GRAPHQL_TYPES.STRING},
        rule_name: {type: GRAPHQL_TYPES.STRING},
        metadata_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
        match_metadata_ids_using_and: {type: GRAPHQL_TYPES.BOOLEAN},
        draft_awaiting_approval: {type: GRAPHQL_TYPES.BOOLEAN},
        template_category_id: {type: GRAPHQL_TYPES.INTEGER},
        template_category_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
        shared: {type: GRAPHQL_TYPES.BOOLEAN},
        start_index: {type: GRAPHQL_TYPES.INTEGER, required: true},
        limit: {type: GRAPHQL_TYPES.INTEGER, required: true}
      }
    };
    const query = gql.query(templatesQuery);
    const variables = {...{start_index: pageInfo.first, limit: pageInfo.rows}, ...searchTerm};
    return this.httpUtilityService.tasksGraphQL<Template[]>(query, templatesQuery.operation, variables).pipe(map(draftPage => ({
      data: draftPage.data.map(draftData => {
        const draft = this.constructTemplateFromResponseData(draftData);
        draft.is_draft = true;
        return draft;
      }),
      count: draftPage.count
    })));
  }

  private templatesOrDraftsFields(scopeOptions: TemplateScopeOptions, templateOrDraft: OPERATION): Fields {
    return  [
      'template_id', 'template_name', 'category', 'primary_metadata_type_ids', 'draft_awaiting_approval', 'draft_id', 'last_modified_at',
      'created_at', 'created_by', 'customer_id', 'priority_id', 'no_calculation', 'template_category_id',
      ...templateOrDraft === OPERATION.TEMPLATES ? ['approved_at', 'approved_by', 'deleted_at', 'is_active', 'shared'] : [],
      ...scopeOptions?.getSensors
        ? [{sensors: ['metadata_id', 'name', 'sensor_id', 'position']}]
        : [],
      ...scopeOptions?.getFunctions
        ? [{functions: ['formula', 'name', 'threshold', 'function_id', 'position']}]
        : [],
      ...scopeOptions?.getProperties
        ? [{properties: ['rule_description', 'rule_name', 'rule_solution']}]
        : [],
      ...scopeOptions?.getFaults
        ? [{fault: ['source_nodes', 'trigger_states', 'position']}]
        : [],
      ...scopeOptions?.getGates
        ? [{gates: ['source_nodes', 'trigger_states', 'type', 'name', 'position']}]
        : [],
      ...scopeOptions?.getRelations
        ? [{relations: ['source_node', 'target_node',
            {state_trigger: ['from', 'to']}]}]
        : []
    ];
  }

  private fetchAllPriorities(): Observable<Priority[]> {
    const pageWrapper = (pageInfo: PageInfo) => this.fetchPrioritiesPagedModel(pageInfo);
    return this.httpUtilityService.pageUntilComplete(pageWrapper, {first: 0, rows: this.PAGE_SIZE});
  }

  private fetchPrioritiesPagedModel(pageInfo: PageInfo): Observable<PagedModel<Priority[]>> {
    const prioritiesQuery = {
      operation: 'priorities',
      fields: ['priority_id', 'name', 'rank'],
      variables: {
        start_index: {type: GRAPHQL_TYPES.INTEGER, required: true},
        limit: {type: GRAPHQL_TYPES.INTEGER, required: true}
      }
    };
    const query = gql.query(prioritiesQuery);
    const variables = {start_index: pageInfo.first, limit: pageInfo.rows};
    return this.httpUtilityService.tasksGraphQL<Priority[]>(query, prioritiesQuery.operation, variables);
  }

  private fetchImpacts(): Observable<Impact[]> {
    const params = new HttpParams().set('start_index', 0);
    const options = {headers: this.headers, params: params};
    return this.http.get(this.getGateway() + '/tasks/impacts/', options).pipe(map(response => {
      const unsortedImpacts: Impact[] = this.httpUtilityService.extractResponseData(response, OPERATION.IMPACTS);
      return unsortedImpacts.sort((a: Impact, b: Impact) => (a?.name ?? '').localeCompare(b?.name ?? ''));
    }));
  }

  getImpacts(): Observable<Impact[]> {
    return this.cachedImpacts.get();
  }

  getPriorities(): Observable<Priority[]> {
    return this.cachedPriorities.get();
  }

  getTemplateDraft(draftId: string): Observable<Template> {
    const options = {headers: this.headers};
    return this.http.get(this.getGateway() + '/tasks/templates/drafts/' + draftId, options).pipe(map(response => {
      const template: Template = this.constructTemplateFromResponse(response, OPERATION.TEMPLATE_DRAFT);
      template.is_draft = true;
      return template;
    }));
  }

  getTemplates(first: number, rows: number, searchParams?: TemplateSearchTerms, scopeOptions?: TemplateScopeOptions): Observable<PagedModel<Template[]>> {
    return this.searchTemplates({first, rows}, searchParams, {getSensors: true, getProperties: true, ...scopeOptions});
  }

  getTemplateDrafts(first: number, rows: number, searchParams?: TemplateSearchTerms, scopeOptions?: TemplateScopeOptions): Observable<PagedModel<Template[]>> {
    return this.searchTemplateDrafts({first, rows}, searchParams, {getSensors: true, getProperties: true, ...scopeOptions});
  }

  /**
   * @returns {Observable<number>} draft id
   */
  upsertTemplateDraft(template: Template): Observable<string> {
    const options = {headers: this.headers};
    let observable;
    if (!template.hasDraft()) {
      observable = this.http.post(this.getGateway() + '/tasks/templates/drafts', template, options);
    } else {
      observable = this.http.put(this.getGateway() + '/tasks/templates/drafts/' + template.draft_id, template, options);
    }
    return observable.pipe(map((response: APIResponse<Template>) => {
      const templateDraft: Template = this.httpUtilityService.extractResponseData(response, OPERATION.TEMPLATE_DRAFT);
      return templateDraft.draft_id;
    }));
  }

  /**
   * @returns {Observable<number>} draft id
   */
  submitForApproval(draftId: string): Observable<string> {
    const options = {headers: this.headers};
    return this.http.post(this.getGateway() + '/tasks/templates/drafts/' + draftId + '/submit', null, options).pipe(map((response: APIResponse<Template>) => {
      const templateDraft: Template = this.httpUtilityService.extractResponseData(response, OPERATION.TEMPLATE_DRAFT);
      return templateDraft.draft_id;
    }));
  }

  /**
   * @returns {Observable<number>} template id
   */
  approveTemplate(draftId: string): Observable<string> {
    const options = {headers: this.headers};
    return this.http.post(this.getGateway() + '/tasks/templates/drafts/' + draftId + '/approve', null, options).pipe(map((response: APIResponse<Template>) => {
      const template: Template = this.httpUtilityService.extractResponseData(response, OPERATION.TEMPLATE);
      return template.template_id;
    }));
  }

  changeSharingTemplate(template: Template): Observable<string> {
    template.shared = !template.shared;
    const options = {headers: this.headers};
    const t: Template = Object.assign({}, template);
    delete t.draft_id;
    delete t.created_at;
    delete t.last_modified_at;
    delete t['deleted_at'];
    delete t.is_active;
    delete t.draft_awaiting_approval;
    delete t['sensorMetadata'];
    delete t.is_draft;

    // TODO Remove once API does this
    if (t.category) {
      t.category = <any>t.category.map(categoryId => {
        return RULE_CATEGORIES.find(category => category.category_id === categoryId).name;
      }).join('/');
    }

    return this.http.put(this.getGateway() + '/tasks/templates/' + t.template_id, t, options)
      .pipe(map((response: APIResponse<Template>) => {
        const template: Template = this.httpUtilityService.extractResponseData(response, OPERATION.TEMPLATE);
        return template.template_id;
      }), catchError(error => {
        template.shared = !template.shared;
        throw error;
      }));
  }

  runTemplateTest(template: Template, values: SensorValue[]): Observable<any> {
    delete template.template_id;
    delete template.draft_id;
    delete template.template_name;
    delete template.properties;

    const options = {headers: this.headers};
    return this.http.post(this.getGateway() + '/tasks/templates/run', {template: template, sensor_data: values}, options)
      .pipe(map((response: APIResponse<Template>) => this.httpUtilityService.extractResponseData(response, OPERATION.TEMPLATE)));
  }

  deleteTemplateDraft(draftId: string): Observable<any> {
    const options = {headers: this.headers};
    return this.http.delete(this.getGateway() + '/tasks/templates/drafts/' + draftId, options);
  }

  deleteTemplate(templateId: string): Observable<any> {
    const options = {headers: this.headers};
    return this.http.delete(this.getGateway() + '/tasks/templates/' + templateId, options);
  }
}
